/**
 * Ledger — `GET payments/ledger/`, scoped to the caller, paginated,
 * read-only (`LedgerViewSet` has no write actions).
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BookOpen } from 'lucide-react';
import { parseApiError } from '../../lib/apiError';
import { useIsMobile } from '../../lib/useIsMobile';
import { EmptyState } from '../../components/EmptyState';
import { listLedgerEntries } from '../../api/endpoints/payments';

export function LedgerPage(): React.JSX.Element {
  const isMobile = useIsMobile();
  const [page, setPage] = useState(1);

  const query = useQuery({
    queryKey: ['ledger', { page }],
    queryFn: () => listLedgerEntries({ page }),
  });

  const entries = query.data?.results ?? [];
  const hasNext = query.data?.next !== null && query.data?.next !== undefined;
  const hasPrevious = query.data?.previous !== null && query.data?.previous !== undefined;

  return (
    <section>
      <h2>Ledger</h2>

      {query.isPending && <p>Loading…</p>}
      {query.isError && (
        <p role="alert" className="form__error">
          {parseApiError(query.error).message}
        </p>
      )}

      {query.isSuccess && entries.length === 0 && (
        <EmptyState icon={BookOpen} message="No ledger entries found." />
      )}

      {query.isSuccess && entries.length > 0 && (
        isMobile ? (
          <ul className="record-cards">
            {entries.map((entry) => (
              <li key={entry.id} className="record-card">
                <div className="record-card__row">
                  <span className="record-card__label">Type</span>
                  <span className="record-card__value">{entry.entry_type}</span>
                </div>
                <div className="record-card__row">
                  <span className="record-card__label">Amount</span>
                  <span className="record-card__value">{entry.amount}</span>
                </div>
                <div className="record-card__row">
                  <span className="record-card__label">Description</span>
                  <span className="record-card__value">{entry.description}</span>
                </div>
                <div className="record-card__row">
                  <span className="record-card__label">Timestamp</span>
                  <span className="record-card__value">{entry.timestamp}</span>
                </div>
                <div className="record-card__row">
                  <span className="record-card__label">Invoice</span>
                  <span className="record-card__value">{entry.invoice ?? '—'}</span>
                </div>
                <div className="record-card__row">
                  <span className="record-card__label">Payment</span>
                  <span className="record-card__value">{entry.payment ?? '—'}</span>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Type</th>
                <th>Amount</th>
                <th>Description</th>
                <th>Timestamp</th>
                <th>Invoice</th>
                <th>Payment</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id}>
                  <td>{entry.entry_type}</td>
                  <td>{entry.amount}</td>
                  <td>{entry.description}</td>
                  <td>{entry.timestamp}</td>
                  <td>{entry.invoice ?? '—'}</td>
                  <td>{entry.payment ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}

      <p>
        <button type="button" disabled={!hasPrevious} onClick={() => setPage((p) => p - 1)}>
          Previous
        </button>{' '}
        <span>Page {page}</span>{' '}
        <button type="button" disabled={!hasNext} onClick={() => setPage((p) => p + 1)}>
          Next
        </button>
      </p>
    </section>
  );
}

export default LedgerPage;
